import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiBook, FiUsers, FiTruck, FiCreditCard,
  FiDownload, FiPrinter, FiEdit2, FiCheck, FiX,
  FiChevronDown,
} from 'react-icons/fi';
import { MdCurrencyRupee } from 'react-icons/md';
import API from '../utils/api';
import toast from 'react-hot-toast';
import BusinessOpeningBalanceModal from '../components/admin/OpeningBalanceModal';

/* ══════════════════════════════════════════════════════════════════
   HELPERS
   ══════════════════════════════════════════════════════════════════ */

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt = (d) =>
  d && d !== '1900-01-01'
    ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

const REF_TYPE_LABELS = {
  opening_balance: 'Opening',
  bill:            'Invoice',
  client_receipt:  'Receipt',
  tds_deduction:   'TDS',
  purchase:        'Purchase',
  vendor_payment:  'Vend. Pay',
  expense:         'Expense',
  credit_wallet:   'Advance',
};

const REF_TYPE_COLORS = {
  opening_balance: { bg: '#f3f4f6',          text: '#6b7280' },
  bill:            { bg: '#eff6ff',          text: '#2563eb' },
  client_receipt:  { bg: '#f0fdf4',          text: '#16a34a' },
  tds_deduction:   { bg: '#fffbeb',          text: '#b45309' },
  purchase:        { bg: '#fff7ed',          text: '#ea580c' },
  vendor_payment:  { bg: '#fdf4ff',          text: '#9333ea' },
  expense:         { bg: '#fef2f2',          text: '#dc2626' },
  credit_wallet:   { bg: '#f0fdfa',          text: '#0d9488' },
};

const DETAIL_PATHS = {
  bill:           (id) => `/admin/billing/${id}`,
  client_receipt: (id) => `/admin/payments`,
  tds_deduction:  () => `/admin/tds`,   // receipts don't have a standalone detail page yet
  purchase:       (id) => `/admin/purchases/${id}`,
  vendor_payment: (id) => `/admin/vendor-payments/${id}`,
  expense:        (id) => `/admin/expenses/${id}`,
};

/* ══════════════════════════════════════════════════════════════════
   SUB-COMPONENTS
   ══════════════════════════════════════════════════════════════════ */

/* ── Summary bar ───────────────────────────────────────────────────── */
const SummaryBar = ({ opening, totalDebit, totalCredit, closing, ledgerType }) => {
  const cards = [
    { label: 'Opening Balance', value: opening,     color: '#6b7280' },
    { label: 'Total Debit',     value: totalDebit,  color: '#dc2626' },
    { label: 'Total Credit',    value: totalCredit, color: '#16a34a' },
    { label: 'Closing Balance', value: closing,     color: closing >= 0 ? '#2563eb' : '#dc2626' },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
      {cards.map(({ label, value, color }) => (
        <div key={label} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 18px' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 800, color, fontFamily: 'var(--font-display)' }}>{money(value)}</div>
        </div>
      ))}
    </div>
  );
};

/* ── Ledger table ──────────────────────────────────────────────────── */
const LedgerTable = ({ entries, loading, navigate }) => {
  if (loading) return <div className="spinner" style={{ margin: '40px auto' }} />;

  if (!entries || entries.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)', background: '#fff', border: '1px solid var(--border)', borderRadius: 10 }}>
        <div style={{ fontSize: '2rem', marginBottom: 8 }}>📒</div>
        <p>No ledger entries found for the selected period.</p>
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: 780 }}>
          <thead>
            <tr style={{ background: 'var(--bg-soft)', borderBottom: '2px solid var(--border)' }}>
              {['Date', 'Ref Type', 'Reference', 'Description', 'Debit', 'Credit', 'Balance'].map((h) => (
                <th key={h} style={{ padding: '11px 14px', textAlign: h === 'Debit' || h === 'Credit' || h === 'Balance' ? 'right' : 'left', fontWeight: 700, fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.map((e, idx) => {
              const isOpening = e.reference_type === 'opening_balance';
              const color = REF_TYPE_COLORS[e.reference_type] || { bg: '#f9fafb', text: '#374151' };
              const detailPath = !isOpening && DETAIL_PATHS[e.reference_type]
                ? DETAIL_PATHS[e.reference_type](e.reference_id)
                : null;

              return (
                <tr
                  key={idx}
                  onClick={() => detailPath && navigate(detailPath)}
                  style={{
                    borderBottom: '1px solid var(--border)',
                    background: isOpening ? '#fafbff' : '#fff',
                    cursor: detailPath ? 'pointer' : 'default',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={(el) => { if (detailPath) el.currentTarget.style.background = 'var(--bg-soft)'; }}
                  onMouseLeave={(el) => { el.currentTarget.style.background = isOpening ? '#fafbff' : '#fff'; }}
                >
                  {/* Date */}
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: 'var(--text-muted)', fontSize: '0.82rem' }}>{fmt(e.date)}</td>

                  {/* Ref type badge */}
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                    <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, background: color.bg, color: color.text }}>
                      {REF_TYPE_LABELS[e.reference_type] || e.reference_type}
                    </span>
                  </td>

                  {/* Reference number */}
                  <td style={{ padding: '10px 14px', fontWeight: 600, color: detailPath ? '#2563eb' : '#374151', whiteSpace: 'nowrap' }}>
                    {e.reference_number || '—'}
                  </td>

                  {/* Description */}
                  <td style={{ padding: '10px 14px', color: '#374151', maxWidth: 280 }}>
                    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {e.description}
                    </span>
                  </td>

                  {/* Debit */}
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: e.debit ? '#dc2626' : 'var(--text-muted)' }}>
                    {e.debit ? money(e.debit) : '—'}
                  </td>

                  {/* Credit */}
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: e.credit ? '#16a34a' : 'var(--text-muted)' }}>
                    {e.credit ? money(e.credit) : '—'}
                  </td>

                  {/* Running balance */}
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: Number(e.balance) < 0 ? '#dc2626' : '#111', whiteSpace: 'nowrap' }}>
                    {money(e.balance)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ── Opening Balance Editor Modal ──────────────────────────────────── */
const OpeningBalanceModal = ({ type, entity, onClose, onSaved }) => {
  const isVendor   = type === 'vendor';
  const isCustomer = type === 'customer';
  const isAccount  = type === 'company_account';

  const [form, setForm] = useState({
    opening_balance:      entity?.opening_balance || entity?.opening_balance || 0,
    opening_balance_type: entity?.opening_balance_type || 'debit',
    opening_balance_date: entity?.opening_balance_date || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    try {
      setSaving(true);
      // Customers use the shared BusinessOpeningBalanceModal (see render below).
      if (isVendor) {
        await API.patch(`/vendors/${entity.id}/opening-balance`, form);
      } else {
        // Company account: use existing PUT route — opening_balance and opening_balance_date are already supported
        await API.put(`/company-bank-accounts/${entity.id}`, {
          opening_balance:      form.opening_balance,
          opening_balance_date: form.opening_balance_date,
        });
      }
      toast.success('Opening balance saved');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 };
  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'var(--font-body)' };

  const entityName = entity?.companyName || entity?.company_name || entity?.account_name || '';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 440, maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Opening Balance</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{entityName}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><FiX size={18} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Opening Balance Amount (₹)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              style={inputStyle}
              value={form.opening_balance}
              onChange={(e) => set('opening_balance', e.target.value)}
              placeholder="0.00"
            />
          </div>

          {/* Balance type: not shown for company account (always positive) */}
          {!isAccount && (
            <div>
              <label style={labelStyle}>Balance Type</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['debit', 'credit'].map((t) => (
                  <button
                    key={t}
                    onClick={() => set('opening_balance_type', t)}
                    style={{
                      flex: 1, padding: '8px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      cursor: 'pointer', fontFamily: 'var(--font-body)',
                      border: form.opening_balance_type === t ? '2px solid #2563eb' : '1px solid #e5e7eb',
                      background: form.opening_balance_type === t ? '#eff6ff' : '#fff',
                      color: form.opening_balance_type === t ? '#2563eb' : '#6b7280',
                    }}
                  >
                    {isCustomer
                      ? t === 'debit' ? '↑ They Owe Us' : '↓ We Owe Them'
                      : t === 'debit' ? '↑ We Owe Them (Payable)' : '↓ They Owe Us (Advance)'}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                {isCustomer
                  ? form.opening_balance_type === 'debit'
                    ? 'Customer had an outstanding receivable before the system started.'
                    : 'Customer had an advance credit before the system started.'
                  : form.opening_balance_type === 'debit'
                    ? 'You had a payable to this vendor before the system started.'
                    : 'Vendor owed you money (advance/over-payment) before the system started.'}
              </div>
            </div>
          )}

          <div>
            <label style={labelStyle}>Opening Balance Date</label>
            <input
              type="date"
              style={inputStyle}
              value={form.opening_balance_date}
              onChange={(e) => set('opening_balance_date', e.target.value)}
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              The ledger will show this as the first row. Only transactions on or after this date are included.
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 22, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 18px', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontFamily: 'var(--font-body)', background: '#fff', fontSize: 14 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '8px 20px', border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body)', background: '#2563eb', color: '#fff', fontWeight: 600, fontSize: 14, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : <><FiCheck size={14} style={{ marginRight: 6 }} />Save</>}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Searchable dropdown ────────────────────────────────────────────── */
const SearchDropdown = ({ options, value, onChange, placeholder, labelKey, valueKey = 'id' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter((o) =>
    String(typeof labelKey === 'function' ? labelKey(o) : o[labelKey]).toLowerCase().includes(q.toLowerCase())
  );

  const selected = options.find((o) => o[valueKey] === value);
  const selectedLabel = selected ? (typeof labelKey === 'function' ? labelKey(selected) : selected[labelKey]) : '';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, fontFamily: 'var(--font-body)', color: selectedLabel ? '#111' : '#9ca3af', textAlign: 'left' }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedLabel || placeholder}</span>
        <FiChevronDown size={14} style={{ flexShrink: 0, marginLeft: 6 }} />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', zIndex: 100, maxHeight: 260, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: 8 }}>
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              style={{ width: '100%', boxSizing: 'border-box', padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, outline: 'none', fontFamily: 'var(--font-body)' }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 && <div style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 13 }}>No results</div>}
            {filtered.map((o) => {
              const label = typeof labelKey === 'function' ? labelKey(o) : o[labelKey];
              const isSelected = o[valueKey] === value;
              return (
                <button
                  key={o[valueKey]}
                  onClick={() => { onChange(o[valueKey], o); setQ(''); setOpen(false); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 13, background: isSelected ? '#eff6ff' : '#fff', color: isSelected ? '#2563eb' : '#111', fontWeight: isSelected ? 600 : 400 }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f9fafb'; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = '#fff'; }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════ */

const TAB_CONFIG = [
  { key: 'customer',        label: 'Customer',        icon: <FiUsers size={15} /> },
  { key: 'vendor',          label: 'Vendor',          icon: <FiTruck size={15} /> },
  { key: 'company_account', label: 'Company Account', icon: <FiCreditCard size={15} /> },
];

const AdminLedger = () => {
  const navigate = useNavigate();

  // ── Tab ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('customer');

  // ── Entity lists ───────────────────────────────────────────────────
  const [businesses, setBusinesses]   = useState([]);
  const [vendors, setVendors]         = useState([]);
  const [accounts, setAccounts]       = useState([]);

  // ── Selections ─────────────────────────────────────────────────────
  const [selectedId, setSelectedId]         = useState(null);
  const [selectedEntity, setSelectedEntity] = useState(null);

  // ── Filters ────────────────────────────────────────────────────────
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]     = useState('');

  // ── Ledger data ────────────────────────────────────────────────────
  const [ledger, setLedger]   = useState(null);
  const [loading, setLoading] = useState(false);

  // ── Opening balance modal ──────────────────────────────────────────
  const [showObModal, setShowObModal] = useState(false);

  // ── Load entity lists on mount ─────────────────────────────────────
  useEffect(() => {
    API.get('/business?limit=500').then((r) => setBusinesses(r.data.businesses || [])).catch(() => {});
    API.get('/vendors?limit=500').then((r) => setVendors(r.data.vendors || [])).catch(() => {});
    API.get('/company-bank-accounts?active=true').then((r) => setAccounts(r.data.accounts || [])).catch(() => {});
  }, []);

  // ── Reset selection when tab changes ──────────────────────────────
  useEffect(() => {
    setSelectedId(null);
    setSelectedEntity(null);
    setLedger(null);
  }, [activeTab]);

  // ── Fetch ledger ───────────────────────────────────────────────────
  const fetchLedger = useCallback(async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set('from_date', fromDate);
      if (toDate)   params.set('to_date',   toDate);

      const endpoint =
        activeTab === 'customer'        ? `/ledger/customer/${selectedId}`
        : activeTab === 'vendor'        ? `/ledger/vendor/${selectedId}`
        : `/ledger/company-account/${selectedId}`;

      const { data } = await API.get(`${endpoint}?${params}`);
      setLedger(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load ledger');
      setLedger(null);
    } finally {
      setLoading(false);
    }
  }, [selectedId, activeTab, fromDate, toDate]);

  // Auto-fetch when selection changes
  useEffect(() => {
    if (selectedId) fetchLedger();
    else setLedger(null);
  }, [selectedId, fetchLedger]);

  // ── Export CSV ─────────────────────────────────────────────────────
  const exportCsv = () => {
    if (!ledger?.entries?.length) return;
    const entityName = selectedEntity?.companyName || selectedEntity?.company_name || selectedEntity?.account_name || 'Ledger';
    const rows = [
      ['Date', 'Ref Type', 'Reference', 'Description', 'Debit', 'Credit', 'Balance'],
      ...ledger.entries.map((e) => [
        e.date,
        REF_TYPE_LABELS[e.reference_type] || e.reference_type,
        e.reference_number || '',
        e.description,
        e.debit || '',
        e.credit || '',
        e.balance,
      ]),
      [],
      ['', '', '', 'Opening Balance', '', '', ledger.opening_balance],
      ['', '', '', 'Total Debit',     ledger.total_debit, '', ''],
      ['', '', '', 'Total Credit',    '', ledger.total_credit, ''],
      ['', '', '', 'Closing Balance', '', '', ledger.closing_balance],
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${entityName.replace(/\s+/g, '_')}_Ledger_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Print ──────────────────────────────────────────────────────────
  const handlePrint = () => window.print();

  /* ─── Entity label helpers ──────────────────────────────────────── */
  const customerLabel = (b) => `${b.companyName || b.contactPersonName}${b.contactPersonPhone ? ` — ${b.contactPersonPhone}` : ''}`;
  const vendorLabel   = (v) => `${v.company_name}${v.vendor_code ? ` (${v.vendor_code})` : ''}`;
  const accountLabel  = (a) => `${a.account_name}${a.bank_name ? ` — ${a.bank_name}` : ''}`;

  const currentEntities = activeTab === 'customer' ? businesses : activeTab === 'vendor' ? vendors : accounts;
  const currentLabel    = activeTab === 'customer' ? customerLabel : activeTab === 'vendor' ? vendorLabel : accountLabel;

  /* ─── Render ──────────────────────────────────────────────────────── */
  return (
    <div>
      {/* ── Page header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 38, height: 38, background: '#eff6ff', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}>
            <FiBook size={18} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800 }}>Ledger</h2>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Account-wise transaction history with running balance</div>
          </div>
        </div>
        {ledger && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={exportCsv} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', fontSize: 13, background: '#fff', fontFamily: 'var(--font-body)' }}>
              <FiDownload size={14} /> Export CSV
            </button>
            <button onClick={handlePrint} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', fontSize: 13, background: '#fff', fontFamily: 'var(--font-body)' }}>
              <FiPrinter size={14} /> Print
            </button>
          </div>
        )}
      </div>

      {/* ── Tab switcher ── */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
        {TAB_CONFIG.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              flex: 1, padding: '11px 0', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              fontFamily: 'var(--font-body)', fontSize: '0.88rem', fontWeight: activeTab === tab.key ? 700 : 500,
              background: activeTab === tab.key ? '#eff6ff' : '#fff',
              color: activeTab === tab.key ? '#2563eb' : 'var(--text-muted)',
              borderBottom: activeTab === tab.key ? '2px solid #2563eb' : '2px solid transparent',
              transition: 'all 0.15s',
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ── Filter bar ── */}
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 12, alignItems: 'end' }}>

          {/* Account selector */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 5 }}>
              {activeTab === 'customer' ? 'Select Customer / Business' : activeTab === 'vendor' ? 'Select Vendor' : 'Select Bank / Cash Account'}
            </label>
            <SearchDropdown
              options={currentEntities}
              value={selectedId}
              onChange={(id, obj) => { setSelectedId(id); setSelectedEntity(obj); }}
              placeholder={`Choose ${activeTab === 'customer' ? 'customer' : activeTab === 'vendor' ? 'vendor' : 'account'}…`}
              labelKey={currentLabel}
            />
          </div>

          {/* From date */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 5 }}>From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              onClick={(e) => e.currentTarget.showPicker?.()}
              style={{ padding: '9px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none', cursor: 'pointer' }}
            />
          </div>

          {/* To date */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 5 }}>To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              onClick={(e) => e.currentTarget.showPicker?.()}
              style={{ padding: '9px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none', cursor: 'pointer' }}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={fetchLedger}
              disabled={!selectedId}
              style={{ padding: '9px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, cursor: selectedId ? 'pointer' : 'not-allowed', fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 13, opacity: selectedId ? 1 : 0.5 }}
            >
              View
            </button>
            {selectedEntity && (
              <button
                onClick={() => setShowObModal(true)}
                title="Set opening balance"
                style={{ padding: '9px 10px', background: '#fff', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontFamily: 'var(--font-body)', color: '#6b7280' }}
              >
                <FiEdit2 size={13} /> Opening Bal
              </button>
            )}
          </div>
        </div>

        {/* Opening balance info strip */}
        {selectedEntity && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)', display: 'flex', gap: 24, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
            {activeTab !== 'company_account' && (
              <>
                <span>
                  Opening Balance: <strong style={{ color: '#111' }}>
                    {money(selectedEntity.opening_balance || 0)}
                  </strong>
                  {selectedEntity.opening_balance_type && (
                    <span style={{ marginLeft: 4, padding: '1px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: selectedEntity.opening_balance_type === 'debit' ? '#fef2f2' : '#f0fdf4', color: selectedEntity.opening_balance_type === 'debit' ? '#dc2626' : '#16a34a' }}>
                      {selectedEntity.opening_balance_type.toUpperCase()}
                    </span>
                  )}
                </span>
                <span>Opening Date: <strong style={{ color: '#111' }}>{fmt(selectedEntity.opening_balance_date)}</strong></span>
              </>
            )}
            {activeTab === 'company_account' && (
              <>
                <span>Opening Balance: <strong style={{ color: '#111' }}>{money(selectedEntity.opening_balance || 0)}</strong></span>
                <span>Opening Date: <strong style={{ color: '#111' }}>{fmt(selectedEntity.opening_balance_date)}</strong></span>
                <span>Account Type: <strong style={{ color: '#111' }}>{selectedEntity.account_type}</strong></span>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Summary bar ── */}
      {ledger && (
        <SummaryBar
          opening={ledger.opening_balance}
          totalDebit={ledger.total_debit}
          totalCredit={ledger.total_credit}
          closing={ledger.closing_balance}
          ledgerType={activeTab}
        />
      )}

      {/* ── Ledger table ── */}
      {(ledger || loading) ? (
        <LedgerTable entries={ledger?.entries || []} loading={loading} navigate={navigate} />
      ) : (
        !selectedId && (
          <div style={{ textAlign: 'center', padding: '64px 0', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>📒</div>
            <div style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 6 }}>Select an account to view its ledger</div>
            <div style={{ fontSize: '0.85rem' }}>Choose a customer, vendor, or bank account from the filter above.</div>
          </div>
        )
      )}

      {/* ── Opening Balance Modal ── */}
      {showObModal && selectedEntity && activeTab === 'customer' && (
        <BusinessOpeningBalanceModal
          business={selectedEntity}
          onClose={() => setShowObModal(false)}
          onSaved={() => {
            API.get('/business?limit=500').then((r) => {
              const list = r.data.businesses || [];
              setBusinesses(list);
              const updated = list.find((b) => b.id === selectedId);
              if (updated) setSelectedEntity(updated);
            });
            fetchLedger();
          }}
        />
      )}
      {showObModal && selectedEntity && activeTab !== 'customer' && (
        <OpeningBalanceModal
          type={activeTab}
          entity={selectedEntity}
          onClose={() => setShowObModal(false)}
          onSaved={() => {
            // Refresh entity list and re-fetch ledger
            if (activeTab === 'customer') {
              API.get('/business?limit=500').then((r) => {
                const list = r.data.businesses || [];
                setBusinesses(list);
                const updated = list.find((b) => b.id === selectedId);
                if (updated) setSelectedEntity(updated);
              });
            } else if (activeTab === 'vendor') {
              API.get('/vendors?limit=500').then((r) => {
                const list = r.data.vendors || [];
                setVendors(list);
                const updated = list.find((v) => v.id === selectedId);
                if (updated) setSelectedEntity(updated);
              });
            } else {
              API.get('/company-bank-accounts?active=true').then((r) => {
                const list = r.data.accounts || [];
                setAccounts(list);
                const updated = list.find((a) => a.id === selectedId);
                if (updated) setSelectedEntity(updated);
              });
            }
            fetchLedger();
          }}
        />
      )}
    </div>
  );
};

export default AdminLedger;